/**
 * SIMPLIFIED APP ROUTER
 *
 * No more ROUTE_TREE duplication!
 * Routes are generated dynamically from backend menu.
 *
 * Benefits:
 * - Single source of truth (backend menu)
 * - No duplication
 * - Less code to maintain
 * - Dynamic permission-based routing
 */

import { Spin } from 'antd';
import type React from 'react';
import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import OrganizationGuard from '../components/guards/OrganizationGuard';
import MainLayout from '../components/layout/MainLayout';
import type { MenuItem } from '../services/authService';
import { useAuthStore } from '../stores/authStore';
import {
  AcceptInvitationPage,
  ErrorPage,
  LoginPage,
  NotFoundPage,
  OrganizationOnboardingPage,
  OrganizationsPage,
  ProfilePage,
  ROUTE_PATHS,
  STATIC_ROUTES,
  WelcomePage,
  getComponentFromPath,
} from './routeConfig';

// ============================================================================
// LOADING COMPONENT
// ============================================================================

const RouteLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '60vh',
      background: 'transparent',
      gap: '16px',
    }}
  >
    <Spin size="large" />
    <div style={{ color: '#666', fontSize: '14px' }}>Loading page...</div>
  </div>
);

// ============================================================================
// DYNAMIC ROUTE GENERATION
// ============================================================================

/**
 * Generate routes from backend menu structure
 * This replaces the old ROUTE_TREE approach
 */
function generateRoutesFromMenu(menuItems: MenuItem[]): React.ReactElement[] {
  const routes: React.ReactElement[] = [];

  for (const item of menuItems) {
    // Recursively process children first
    if (item.children && item.children.length > 0) {
      routes.push(...generateRoutesFromMenu(item.children));
    }

    // If item has a path, create a route
    if (item.path) {
      const Component = getComponentFromPath(item.path);

      if (Component) {
        routes.push(
          <Route
            key={item.path}
            path={item.path}
            element={
              <Suspense fallback={<RouteLoader />}>
                <Component />
              </Suspense>
            }
          />,
        );
      } else {
        console.warn(`[Router] No component found for path: ${item.path}`);
      }
    }
  }

  return routes;
}

// ============================================================================
// APP ROUTER COMPONENT
// ============================================================================

const AppRouter: React.FC = () => {
  const { user } = useAuthStore();

  // Generate routes dynamically from backend menu
  // This is the magic - no ROUTE_TREE duplication needed!
  const dynamicRoutes = user?.menu ? generateRoutesFromMenu(user.menu) : [];

  return (
    <Routes>
      {/* ====================================================================
          PUBLIC ROUTES
          ==================================================================== */}

      <Route
        path={ROUTE_PATHS.LOGIN}
        element={
          <Suspense fallback={<RouteLoader />}>
            <LoginPage />
          </Suspense>
        }
      />

      {/* Legacy route redirects */}
      <Route path="/sign-in" element={<Navigate to={ROUTE_PATHS.LOGIN} replace />} />
      <Route path="/sign-up" element={<Navigate to={ROUTE_PATHS.LOGIN} replace />} />
      <Route path="/register" element={<Navigate to={ROUTE_PATHS.LOGIN} replace />} />

      <Route
        path={ROUTE_PATHS.ACCEPT_INVITATION}
        element={
          <Suspense fallback={<RouteLoader />}>
            <AcceptInvitationPage />
          </Suspense>
        }
      />

      {/* ====================================================================
          ONBOARDING ROUTES (Requires auth but no organization)
          ==================================================================== */}

      <Route
        path={ROUTE_PATHS.ONBOARDING}
        element={
          <ProtectedRoute>
            <Suspense fallback={<RouteLoader />}>
              <WelcomePage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTE_PATHS.ONBOARDING_ORGANIZATION}
        element={
          <ProtectedRoute>
            <Suspense fallback={<RouteLoader />}>
              <OrganizationOnboardingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ====================================================================
          PROTECTED ROUTES WITH LAYOUT
          ==================================================================== */}

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <OrganizationGuard>
              <MainLayout />
            </OrganizationGuard>
          </ProtectedRoute>
        }
      >
        {/* Default redirect */}
        <Route index element={<Navigate to={ROUTE_PATHS.DASHBOARD} replace />} />

        {/* ================================================================
            DYNAMIC ROUTES FROM BACKEND MENU

            These routes are generated from the backend menu structure.
            Backend controls:
            - What routes exist
            - What appears in navigation
            - Permission filtering

            Frontend only needs to know which component to render.
            ================================================================ */}

        {dynamicRoutes}

        {/* ================================================================
            STATIC ROUTES (Not in menu but accessible)

            These routes don't appear in the navigation menu but are
            accessible directly (profile, settings, etc.)
            ================================================================ */}

        {STATIC_ROUTES.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <Suspense fallback={<RouteLoader />}>
                <route.component />
              </Suspense>
            }
          />
        ))}

        {/* ================================================================
            ERROR ROUTES
            ================================================================ */}

        <Route
          path={ROUTE_PATHS.UNAUTHORIZED}
          element={
            <Suspense fallback={<RouteLoader />}>
              <ErrorPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTE_PATHS.SERVER_ERROR}
          element={
            <Suspense fallback={<RouteLoader />}>
              <ErrorPage />
            </Suspense>
          }
        />

        {/* 404 Catch-all - Must be last */}
        <Route
          path="*"
          element={
            <Suspense fallback={<RouteLoader />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
};

export default AppRouter;
